import { Wind, Activity, Flower2, Brain, Music, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/I18nProvider";

const PRACTICES = [
    {
        id: "breathwork",
        titleKey: "prac.breath.title",
        subtitleKey: "prac.breath.sub",
        textKey: "prac.breath.text",
        icon: Wind,
        accent: "#3D8C95",
        image: "https://images.unsplash.com/photo-1693936100383-ece0800ca39f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxxaSUyMGdvbmclMjBicmVhdGh3b3JrJTIwbmF0dXJlfGVufDB8fHx8MTc4MTQwNzA0OXww&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "somatique",
        titleKey: "prac.somatic.title",
        subtitleKey: "prac.somatic.sub",
        textKey: "prac.somatic.text",
        icon: Activity,
        accent: "#3D8C95",
        image: "https://images.unsplash.com/photo-1741522509438-a120c0bb5e88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxtYXNzYWdlJTIwdGhlcmFweSUyMHJlbGF4fGVufDB8fHx8MTc4MTQwNzA0OXww&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "qi-gong",
        titleKey: "prac.qigong.title",
        subtitleKey: "prac.qigong.sub",
        textKey: "prac.qigong.text",
        icon: Flower2,
        accent: "#3D8C95",
        image: "https://images.unsplash.com/photo-1639162906614-0603b0ae95fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwzfHxtYXNzYWdlJTIwdGhlcmFweSUyMHJlbGF4fGVufDB8fHx8MTc4MTQwNzA0OXww&ixlib=rb-4.1.0&q=85",
    },
];

const INSTRUMENTS = [
    {
        name: "Kalimba",
        descKey: "inst.kalimba.desc",
        image: "https://images.unsplash.com/photo-1671108801041-8d3c4f1bed77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwzfHxzb3VuZCUyMGhlYWxpbmclMjBoYW5kcGFuJTIwa2FsaW1iYXxlbnwwfHx8fDE3ODE0MDcwNDl8MA&ixlib=rb-4.1.0&q=85",
        gradient: null,
        audio: "https://cdn.pixabay.com/download/audio/2022/03/15/audio_1cf389cce0.mp3",
    },
    {
        name: "Tongue Drum",
        descKey: "inst.tongue.desc",
        image: "https://images.unsplash.com/photo-1740208375749-d4fe0f651e00?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxzb3VuZCUyMGhlYWxpbmclMjBoYW5kcGFuJTIwa2FsaW1iYXxlbnwwfHx8fDE3ODE0MDcwNDl8MA&ixlib=rb-4.1.0&q=85",
        gradient: null,
        audio: "https://cdn.pixabay.com/download/audio/2024/03/01/audio_6cf2e8d3e1.mp3",
    },
    {
        name: "Carillon",
        descKey: "inst.carillon.desc",
        image: null,
        gradient: "linear-gradient(135deg, #3D8C95 0%, #5BA8B1 45%, #E8E2D9 100%)",
        audio: "https://cdn.pixabay.com/download/audio/2022/10/30/audio_d24bce5d44.mp3",
    },
];

export default function Exercices() {
    const { t } = useT();
    return (
        <main data-testid="exercices-page" className="overflow-hidden">
            {/* Hero */}
            <section className="relative pt-32 md:pt-40 pb-16 md:pb-24">
                <div className="absolute inset-0 -z-10">
                    <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-[#C44569]/10 blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#3D8C95]/15 blur-3xl" />
                </div>

                <div className="max-w-5xl mx-auto px-6 md:px-12 text-center fade-up">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E8E2D9] mb-6">
                        <Brain size={14} className="text-[#3D8C95]" />
                        <span className="text-xs tracking-[0.2em] uppercase text-[#6E6862]">
                            {t("ex.tag")}
                        </span>
                    </div>
                    <h1
                        data-testid="exercices-title"
                        className="font-display text-5xl md:text-6xl lg:text-7xl font-light text-[#2C2A29] tracking-tight leading-[1.05]"
                    >
                        {t("ex.title1")}
                        <span className="block italic text-[#3D8C95]">
                            {t("ex.title2")}
                        </span>
                    </h1>
                    <p className="mt-6 text-base md:text-lg text-[#6E6862] leading-relaxed max-w-2xl mx-auto">
                        {t("ex.desc")}
                    </p>
                </div>
            </section>

            {/* Practices alternating */}
            <section
                data-testid="practices-section"
                className="py-16 md:py-24"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-24 md:space-y-32">
                    {PRACTICES.map((p, i) => {
                        const Icon = p.icon;
                        const reverse = i % 2 === 1;
                        return (
                            <div
                                key={p.id}
                                data-testid={`practice-${p.id}`}
                                className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
                                    reverse ? "lg:[&>div:first-child]:order-2" : ""
                                }`}
                            >
                                <div className="relative">
                                    <div
                                        className="absolute -inset-4 rounded-[2rem] blur-2xl opacity-30"
                                        style={{ background: p.accent }}
                                    />
                                    <img
                                        src={p.image}
                                        alt={t(p.titleKey)}
                                        className="relative rounded-[2rem] w-full aspect-[4/5] object-cover shadow-2xl"
                                    />
                                </div>
                                <div>
                                    <div
                                        className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                                        style={{ background: `${p.accent}15` }}
                                    >
                                        <Icon size={24} style={{ color: p.accent }} />
                                    </div>
                                    <div
                                        className="text-xs tracking-[0.3em] uppercase mb-3"
                                        style={{ color: p.accent }}
                                    >
                                        {t(p.subtitleKey)}
                                    </div>
                                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-tight">
                                        {t(p.titleKey)}
                                    </h2>
                                    <p className="mt-5 text-[#6E6862] leading-relaxed text-base md:text-lg">
                                        {t(p.textKey)}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Sophrologie theory */}
            <section
                data-testid="sophrologie-section"
                className="py-20 md:py-28 bg-[#2C2A29] text-white relative overflow-hidden"
            >
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#E07A5F] rounded-full blur-3xl" />
                </div>
                <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#E07A5F] mb-4">
                        {t("ex.sophro_eyebrow")}
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-tight">
                        {t("ex.sophro_title1")}{" "}
                        <span className="italic text-[#3D8C95]">{t("ex.sophro_title2")}</span>
                    </h2>
                    <p className="mt-6 text-base md:text-lg text-white/75 leading-relaxed max-w-3xl mx-auto">
                        {t("ex.sophro_desc")}
                    </p>
                </div>
            </section>

            {/* Musicothérapie / instruments */}
            <section
                id="musicotherapie"
                data-testid="instruments-section"
                className="py-20 md:py-28"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="max-w-3xl mb-14">
                        <div className="flex items-center gap-3 mb-4">
                            <Music size={18} className="text-[#3D8C95]" />
                            <span className="text-xs tracking-[0.3em] uppercase text-[#3D8C95]">
                                {t("ex.mus_eyebrow")}
                            </span>
                        </div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-tight">
                            {t("ex.mus_title1")}{" "}
                            <span className="italic text-[#3D8C95]">{t("ex.mus_title2")}</span>.
                        </h2>
                        <p className="mt-5 text-[#6E6862] leading-relaxed text-base md:text-lg">
                            {t("ex.mus_desc")}
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {INSTRUMENTS.map((ins) => (
                            <div
                                key={ins.name}
                                data-testid={`instrument-${ins.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="group relative rounded-3xl overflow-hidden bg-white border border-[#E8E2D9] shadow-[0_8px_30px_rgba(44,42,41,0.04)] hover:shadow-[0_20px_60px_rgba(44,42,41,0.1)] transition-all duration-500 hover:-translate-y-1"
                            >
                                <div
                                    className="relative aspect-[4/3] flex items-center justify-center"
                                    style={ins.gradient ? { background: ins.gradient } : undefined}
                                >
                                    {ins.image ? (
                                        <img
                                            src={ins.image}
                                            alt={ins.name}
                                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    ) : (
                                        <div className="relative text-white text-center">
                                            <Music size={56} className="mx-auto opacity-90" />
                                            <div className="font-display text-3xl mt-3 opacity-95">
                                                {ins.name}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="p-7">
                                    <h3 className="font-display text-3xl text-[#2C2A29]">
                                        {ins.name}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#6E6862] leading-relaxed">
                                        {t(ins.descKey)}
                                    </p>
                                    {ins.audio && (
                                        <audio
                                            data-testid={`audio-${ins.name.toLowerCase().replace(/\s+/g, "-")}`}
                                            controls
                                            preload="none"
                                            className="mt-5 w-full"
                                        >
                                            <source src={ins.audio} type="audio/mpeg" />
                                        </audio>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA back to booking */}
            <section className="py-20 md:py-28 text-center">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <h2 className="font-display text-4xl md:text-5xl text-[#2C2A29] leading-tight">
                        {t("ex.cta_title")}
                    </h2>
                    <p className="mt-4 text-[#6E6862] text-base md:text-lg">
                        {t("ex.cta_desc")}
                    </p>
                    <a href="/#reservation">
                        <Button
                            data-testid="exercices-cta-book"
                            className="mt-8 rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6 text-base transition-transform hover:-translate-y-1 shadow-[0_10px_30px_rgba(224,122,95,0.3)]"
                        >
                            {t("hero.cta_book")}
                        </Button>
                    </a>
                </div>
            </section>
        </main>
    );
}
